import {StandardDateRange} from "@/lib/queries/reports.ts";
import {endOfDay, isEqual, isValid, parse, startOfDay} from "date-fns";
import {format} from "date-fns-tz";
import {TZDate} from "react-day-picker";
import {getDatepickerStandardRangeOption} from "./subComponents/DatePickerShortcutList/DatePickerShortcutList.utils.ts";

/**
 * The compact form legacy reads and writes for date ranges
 * (uptime/apps/lib/daterange.py:162). Kept so links stay interchangeable between the two frontends.
 */
const LEGACY_DATE_FORMAT = "yyyyMMdd";
const LEGACY_DATE_PATTERN = /^\d{8}$/;

/** How a from/to pair is written into the query string. */
export type DateRangeParamFormat = "iso" | "legacyDays";

const inTimezone = (date: Date, timezone?: string): Date => (timezone ? new TZDate(date, timezone) : date);

/**
 * Read one end of a range out of the URL. Under `legacyDays` the compact form names a whole day, so
 * it expands to that day's bounds in the given timezone — the timezone the backend reads it in.
 */
export const parseDateRangeParam = (
  value: string | null,
  boundary: "start" | "end",
  timezone?: string,
  paramFormat: DateRangeParamFormat = "iso"
): Date | null => {
  if (!value) return null;
  if (LEGACY_DATE_PATTERN.test(value)) {
    if (paramFormat === "legacyDays") {
      const day = parse(value, LEGACY_DATE_FORMAT, inTimezone(new Date(), timezone));
      if (!isValid(day)) return null;
      return boundary === "start" ? startOfDay(day) : endOfDay(day);
    }
    // `iso` callers keep the original reading: viewer-local midnight at both ends, no expansion.
    // It is the wrong reading for a form naming whole days, but the report and check-analysis pages
    // have resolved these links this way for years and correcting it here would move their totals.
    const day = parse(value, LEGACY_DATE_FORMAT, new Date());
    return isValid(day) ? day : null;
  }
  const instant = inTimezone(new Date(value), timezone);
  return isValid(instant) ? instant : null;
};

/**
 * Write a from/to pair into the query string.
 *
 * `legacyDays` emits the compact form only when the range covers whole days in the given timezone,
 * so a link produced here also opens on the legacy status page; anything finer keeps its exact
 * instant, which the compact form cannot hold.
 */
export const formatDateRangeParams = (
  from: Date,
  to: Date,
  paramFormat: DateRangeParamFormat,
  timezone?: string
): {from: string; to: string} => {
  if (paramFormat === "legacyDays") {
    const zonedFrom = inTimezone(from, timezone);
    const zonedTo = inTimezone(to, timezone);
    if (isEqual(zonedFrom, startOfDay(zonedFrom)) && isEqual(zonedTo, endOfDay(zonedTo))) {
      return {
        from: format(zonedFrom, LEGACY_DATE_FORMAT),
        to: format(zonedTo, LEGACY_DATE_FORMAT),
      };
    }
  }
  return {from: from.toISOString(), to: to.toISOString()};
};

/** Expand a day-granular range to cover both days in full, in the given timezone. */
export const wholeDayRange = (from: Date, to: Date): {from: Date; to: Date} => ({
  from: startOfDay(from),
  to: endOfDay(to),
});

/**
 * Widen a day-granular selection to whole days.
 *
 * The picker returns mixed precision for the same nominal range — the calendar lands on 00:00 of
 * each day, dropping the final day, while the "Last N days" shortcuts end at end-of-day or at the
 * current time. Sub-day shortcuts are left alone: their start is not a day boundary, and widening
 * them would turn a four-hour selection into a whole day.
 */
export const normalizeSelectedRange = (from: Date, to: Date): {from: Date; to: Date} =>
  isEqual(from, startOfDay(from)) ? wholeDayRange(from, to) : {from, to};

// The shortcuts a range expressed in whole days can be named by. Sub-day shortcuts are excluded on
// purpose: their end is the exact instant of the click, so they could only ever match in that
// instant and the name would decay into timestamps on the next reload.
const DAY_SHORTCUTS: StandardDateRange[] = [
  StandardDateRange.LAST_7_DAYS,
  StandardDateRange.LAST_14_DAYS,
  StandardDateRange.LAST_30_DAYS,
  StandardDateRange.LAST_90_DAYS,
];

/**
 * Name a window after the quick pick that produces it, for callers that store concrete dates rather
 * than a shortcut token. Spans come from the shortcut options themselves so redefining one cannot
 * leave this mislabelling, and both sides go through {@link normalizeSelectedRange} first because
 * "Last 14 days" ends at the current time while the others end at end-of-day.
 */
export const matchDayShortcut = (from: Date, to: Date, timezone?: string): StandardDateRange | undefined => {
  const now = inTimezone(new Date(), timezone);
  return DAY_SHORTCUTS.find((value) => {
    const option = getDatepickerStandardRangeOption(value);
    if (!option) return false;
    const shortcut = normalizeSelectedRange(option.getStartDate(now), option.getEndDate(now));
    return isEqual(shortcut.from, from) && isEqual(shortcut.to, to);
  });
};
